import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";

import Badge from "../../ui/badge/Badge";
import { formatCurrencyValue } from "../../../utils/formatters";
import { Copy, Check, ExternalLink } from 'lucide-react';
import api from "../../../services/api";
import authService from "../../../services/authService";
import Pagination from "../../ui/pagination/Pagination";

interface Offer {
    id: string;
    offerId: string;
    productId: string;
    name: string;
    slug: string;
    customUrl: string | null;
    price: string;
    currency: string;
    active: boolean;
    isDefault: boolean;
    type: string;
    createdAt: string;
    updatedAt: string;
    paymentLink?: PaymentLink[];
    status: string;
}

interface PaymentLink {
    id: string;
    paymentLinkId: string;
    offerId: string;
    code: string;
    fullUrl: string;
    name: string | null;
    status: string;
    type: string;
    createdAt: string;
    updatedAt: string;
}

interface ViewProductOffersTableProps {
    productId?: string;
}

const currencySymbols: Record<string, string> = {
    BRL: 'R$',
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
};

export default function ViewProductOffersTable({ productId }: ViewProductOffersTableProps) {
    const [offers, setOffers] = useState<Offer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        const fetchProductOffers = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await api.get(`/api/products/${productId}/offers-with-links`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.offers) {
                    // Filtrar ofertas para remover aquelas com status "deleted"
                    const filteredOffers = response.data.offers.filter((offer: Offer) =>
                        offer.status !== "deleted" && offer.active
                    );
                    setOffers(filteredOffers);
                } else {
                    setOffers([]);
                }
            } catch (err: unknown) {
                console.error("Erro ao buscar ofertas do produto:", err);
                setError(err instanceof Error ? err.message : "Erro ao carregar ofertas do produto");
            } finally {
                setLoading(false);
            }
        };

        if (productId) {
            fetchProductOffers();
        }
    }, [productId]);

    // Função para obter o link de venda
    const generateSalesLink = (offer: Offer) => {
        if (offer.paymentLink && offer.paymentLink.length > 0) {
            const paymentLink = offer.paymentLink.find(link => link.fullUrl);
            if (paymentLink && paymentLink.fullUrl) {
                return paymentLink.fullUrl;
            }
        }

        if (offer.customUrl) {
            return offer.customUrl;
        }

        return null;
    };

    // Função para copiar link
    const handleCopyLink = async (offer: Offer) => {
        const link = generateSalesLink(offer);
        if (link) {
            try {
                await navigator.clipboard.writeText(link);
                setCopiedId(offer.id);
                setTimeout(() => setCopiedId(null), 2000);
            } catch (err) {
                console.error("Erro ao copiar link:", err);
            }
        }
    };

    // Função para abrir link em nova aba
    const handleOpenLink = (offer: Offer) => {
        const link = generateSalesLink(offer);
        if (link) {
            window.open(link, '_blank');
        }
    };

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Cálculo para paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = offers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(offers.length / itemsPerPage);

    if (loading) {
        return (
            <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-gray-500 dark:text-gray-400">Carregando ofertas...</p>
            </div>
        );
    }

    if (error) {
        return <div className="p-4 text-center text-red-500">Erro: {error}</div>;
    }

    if (offers.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                Nenhuma oferta ativa encontrada para este produto.
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-xl">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Oferta
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Preço
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Status
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Link de Venda
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400"
                            >
                                Ações
                            </TableCell>
                        </TableRow>
                    </TableHeader>

                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {currentItems.map((offer) => {
                            const salesLink = generateSalesLink(offer);
                            const isCopied = copiedId === offer.id;

                            return (
                                <TableRow key={offer.id}>
                                    <TableCell className="px-5 py-4 text-start">
                                        <div>
                                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                {offer.name}
                                            </span>
                                            <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                                ID: {offer.offerId}
                                            </span>
                                            {offer.isDefault && (
                                                <span className="mt-1 inline-block">
                                                    <Badge size="sm" color="info">
                                                        Padrão
                                                    </Badge>
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <span className="font-medium text-gray-800 dark:text-white/90">
                                            {currencySymbols[offer.currency] || offer.currency} {formatCurrencyValue(parseFloat(offer.price), offer.currency)}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={offer.active ? "success" : "error"}
                                        >
                                            {offer.active ? "Ativo" : "Inativo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-start">
                                        {salesLink ? (
                                            <div className="max-w-xs">
                                                <p className="text-sm text-gray-600 dark:text-gray-300 truncate" title={salesLink}>
                                                    {salesLink}
                                                </p>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-sm">Link não disponível</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-center">
                                        {salesLink && (
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleCopyLink(offer)}
                                                    className={`p-2 rounded-lg transition-colors ${isCopied
                                                        ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                                                        }`}
                                                    title={isCopied ? "Copiado!" : "Copiar link"}
                                                >
                                                    {isCopied ? (
                                                        <Check className="size-4" />
                                                    ) : (
                                                        <Copy className="size-4" />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleOpenLink(offer)}
                                                    className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                                                    title="Abrir link"
                                                >
                                                    <ExternalLink className="size-4" />
                                                </button>
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                {totalPages > 1 && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
